/**
 * FeedFilter Component
 * 
 * Comprehensive filter component for feed timeline with category, job level, and author filters
 * Responsive design: sidebar on desktop, top section on mobile
 */

import React, { useState, useMemo } from 'react';
import {
    Box,
    VStack,
    Select,
    Text,
    Button,
    useColorModeValue,
    Skeleton,
    Alert,
    AlertIcon,
    HStack,
    Input,
    List,
    ListItem,
    InputGroup,
    InputRightElement,
    IconButton,
    Divider,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useCategories } from './hooks/useCategories';
import { useFilterOptions } from './hooks/useFilterOptions';
import { Category, JobLevel } from './types';

interface FeedFilterProps {
    selectedCategoryId?: number;
    selectedJobLevelId?: number;
    selectedAuthorName?: string;
    onApplyFilters: (filters: {
        categoryId?: number;
        jobLevelId?: number;
        authorName?: string;
    }) => void;
    isMobile?: boolean;
}

export const FeedFilter: React.FC<FeedFilterProps> = ({
    selectedCategoryId,
    selectedJobLevelId,
    selectedAuthorName,
    onApplyFilters,
    isMobile = false,
}) => {
    const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useCategories();
    const { data: filterOptions, isLoading: optionsLoading, error: optionsError } = useFilterOptions();
    
    // Local state for filter inputs (not applied yet)
    const [localCategoryId, setLocalCategoryId] = useState<number | undefined>(selectedCategoryId);
    const [localJobLevelId, setLocalJobLevelId] = useState<number | undefined>(selectedJobLevelId);
    const [authorInput, setAuthorInput] = useState(selectedAuthorName || '');
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    const bgColor = useColorModeValue('white', 'gray.800');
    const bgFilterColor = useColorModeValue('gray.50', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const suggestionBg = useColorModeValue('white', 'gray.700');
    const suggestionHoverBg = useColorModeValue('gray.100', 'gray.600');

    // Filter suggestions based on input
    const filteredAuthors = useMemo(() => {
        if (!filterOptions?.users || !authorInput) return [];
        
        const searchTerm = authorInput.toLowerCase();
        return filterOptions.users
            .filter(user => user.toLowerCase().includes(searchTerm))
            .slice(0, 10); // Limit to 10 suggestions
    }, [filterOptions?.users, authorInput]);

    const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setLocalCategoryId(value ? parseInt(value) : undefined);
    };

    const handleJobLevelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setLocalJobLevelId(value ? parseInt(value) : undefined);
    };

    const handleAuthorInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAuthorInput(event.target.value);
        setShowSuggestions(true);
    };

    const handleAuthorSelect = (author: string) => {
        setAuthorInput(author);
        setShowSuggestions(false);
    };

    const handleAuthorClear = () => {
        setAuthorInput('');
        setShowSuggestions(false);
    };

    const handleClearAllFilters = () => {
        setLocalCategoryId(undefined);
        setLocalJobLevelId(undefined);
        setAuthorInput('');
    };

    const handleApplyFilters = () => {
        onApplyFilters({
            categoryId: localCategoryId,
            jobLevelId: localJobLevelId,
            authorName: authorInput.trim() || undefined,
        });
    };

    const hasLocalFilters = localCategoryId || localJobLevelId || authorInput.trim();
    const hasAppliedFilters = selectedCategoryId || selectedJobLevelId || selectedAuthorName;

    // Check if there are unapplied changes
    const hasUnappliedChanges =
        localCategoryId !== selectedCategoryId ||
        localJobLevelId !== selectedJobLevelId ||
        (authorInput.trim() || undefined) !== selectedAuthorName;

    const isLoading = categoriesLoading || optionsLoading;
    const hasError = categoriesError || optionsError;

    if (isLoading) {
        return (
            <Box
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                p={4}
                w={isMobile ? "100%" : "280px"}
            >
                <VStack spacing={3} align="stretch">
                    <Skeleton height="20px" width="120px" />
                    <Skeleton height="40px" />
                    <Skeleton height="40px" />
                    <Skeleton height="40px" />
                    <Skeleton height="32px" />
                </VStack>
            </Box>
        );
    }

    if (hasError) {
        return (
            <Box
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                p={4}
                w={isMobile ? "100%" : "280px"}
            >
                <Alert status="error" size="sm">
                    <AlertIcon />
                    <Text fontSize="sm">Gagal memuat filter</Text>
                </Alert>
            </Box>
        );
    }

    // Mobile Layout: Horizontal with wrapping
    if (isMobile) {
        return (
            <Box
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={3}
                w="100%"
            >
                <VStack spacing={3} align="stretch">
                    {/* Header */}
                    <HStack justify="space-between">
                        <Text fontWeight="bold" fontSize="md" color="customGreen.500">
                            Filter
                        </Text>
                        {hasLocalFilters && (
                            <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                onClick={handleClearAllFilters}
                            >
                                Reset Semua
                            </Button>
                        )}
                    </HStack>

                    {/* Filters in columns on mobile */}
                    <VStack spacing={2} align="stretch">
                        {/* Category Filter */}
                        <Box>
                            <Text fontSize="xs" mb={1} color="gray.600">
                                Kategori
                            </Text>
                            <Select
                                placeholder="Semua Kategori"
                                size="sm"
                                value={localCategoryId || ''}
                                onChange={handleCategoryChange}
                                bg={bgFilterColor}
                                borderColor={borderColor}
                                borderRadius="md"
                            >
                                {categories?.map((category: Category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        {/* Job Level Filter */}
                        <Box>
                            <Text fontSize="xs" mb={1} color="gray.600">
                                Level Jabatan
                            </Text>
                            <Select
                                placeholder="Semua Level"
                                size="sm"
                                value={localJobLevelId || ''}
                                onChange={handleJobLevelChange}
                                bg={bgFilterColor}
                                borderColor={borderColor}
                                borderRadius="md"
                            >
                                {filterOptions?.job_levels.map((jobLevel: JobLevel) => (
                                    <option key={jobLevel.id} value={jobLevel.id}>
                                        {jobLevel.name}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        {/* Author Name Autocomplete */}
                        <Box position="relative">
                            <Text fontSize="xs" mb={1} color="gray.600">
                                Nama Petugas
                            </Text>
                            <InputGroup size="sm">
                                <Input
                                    placeholder="Ketik nama..."
                                    value={authorInput}
                                    onChange={handleAuthorInputChange}
                                    onFocus={() => setShowSuggestions(true)}
                                    onBlur={() => {
                                        // Delay to allow click on suggestion
                                        setTimeout(() => setShowSuggestions(false), 200);
                                    }}
                                    bg={bgFilterColor}
                                    borderColor={borderColor}
                                    borderRadius="md"
                                />
                                {authorInput && (
                                    <InputRightElement>
                                        <IconButton
                                            aria-label="Clear author"
                                            icon={<CloseIcon />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={handleAuthorClear}
                                        />
                                    </InputRightElement>
                                )}
                            </InputGroup>

                            {/* Suggestions Dropdown */}
                            {showSuggestions && filteredAuthors.length > 0 && (
                                <List
                                    position="absolute"
                                    top="100%"
                                    left={0}
                                    right={0}
                                    bg={suggestionBg}
                                    border="1px"
                                    borderColor={borderColor}
                                    borderRadius="md"
                                    mt={1}
                                    maxH="200px"
                                    overflowY="auto"
                                    zIndex={1000}
                                    boxShadow="md"
                                >
                                    {filteredAuthors.map((author, index) => (
                                        <ListItem
                                            key={index}
                                            px={3}
                                            py={2}
                                            fontSize="sm"
                                            cursor="pointer"
                                            _hover={{ bg: suggestionHoverBg }}
                                            onMouseDown={() => handleAuthorSelect(author)}
                                        >
                                            {author}
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </VStack>

                    {/* Apply Button */}
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                        <Button
                            size="sm"
                            colorScheme="green"
                            bg="customGreen.500"
                            _hover={{ bg: "customGreen.600" }}
                            onClick={handleApplyFilters}
                            isDisabled={!hasUnappliedChanges}
                        >
                            Terapkan
                        </Button>
                    </Box>
                </VStack>
            </Box>
        );
    }

    // Desktop Layout: Vertical Sidebar
    return (
        <Box
            bg={bgColor}
            border="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
            w="280px"
        >
            <VStack spacing={4} align="stretch">
                {/* Header */}
                <HStack justify="space-between">
                    <Text fontWeight="bold" fontSize="md" color="customGreen.500">
                        Filter
                    </Text>
                    {hasLocalFilters && (
                        <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            onClick={handleClearAllFilters}
                        >
                            Reset
                        </Button>
                    )}
                </HStack>

                <Divider />

                {/* Category Filter */}
                <Box>
                    <Text fontSize="sm" mb={2} fontWeight="medium">
                        Kategori
                    </Text>
                    <Select
                        placeholder="Semua Kategori"
                        size="sm"
                        value={localCategoryId || ''}
                        onChange={handleCategoryChange}
                        bg={bgFilterColor}
                        borderColor={borderColor}
                        borderRadius="md"
                        _hover={{
                            borderColor: 'customGreen.300',
                        }}
                        _focus={{
                            borderColor: 'customGreen.500',
                            boxShadow: '0 0 0 1px #4A5A33',
                        }}
                    >
                        {categories?.map((category: Category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </Select>
                </Box>

                {/* Job Level Filter */}
                <Box>
                    <Text fontSize="sm" mb={2} fontWeight="medium">
                        Level Jabatan
                    </Text>
                    <Select
                        placeholder="Semua Level"
                        size="sm"
                        value={localJobLevelId || ''}
                        onChange={handleJobLevelChange}
                        bg={bgFilterColor}
                        borderColor={borderColor}
                        borderRadius="md"
                        _hover={{
                            borderColor: 'customGreen.300',
                        }}
                        _focus={{
                            borderColor: 'customGreen.500',
                            boxShadow: '0 0 0 1px #4A5A33',
                        }}
                    >
                        {filterOptions?.job_levels.map((jobLevel: JobLevel) => (
                            <option key={jobLevel.id} value={jobLevel.id}>
                                {jobLevel.name}
                            </option>
                        ))}
                    </Select>
                </Box>

                {/* Author Name Autocomplete */}
                <Box position="relative">
                    <Text fontSize="sm" mb={2} fontWeight="medium">
                        Nama Petugas
                    </Text>
                    <InputGroup size="sm">
                        <Input
                            placeholder="Ketik nama..."
                            value={authorInput}
                            onChange={handleAuthorInputChange}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => {
                                // Delay to allow click on suggestion
                                setTimeout(() => setShowSuggestions(false), 200);
                            }}
                            bg={bgFilterColor}
                            borderColor={borderColor}
                            borderRadius="md"
                            _hover={{
                                borderColor: 'customGreen.300',
                            }}
                            _focus={{
                                borderColor: 'customGreen.500',
                                boxShadow: '0 0 0 1px #4A5A33',
                            }}
                        />
                        {authorInput && (
                            <InputRightElement>
                                <IconButton
                                    aria-label="Clear author"
                                    icon={<CloseIcon />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={handleAuthorClear}
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && filteredAuthors.length > 0 && (
                        <List
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            bg={suggestionBg}
                            border="1px"
                            borderColor={borderColor}
                            borderRadius="md"
                            mt={1}
                            maxH="200px"
                            overflowY="auto"
                            zIndex={1000}
                            boxShadow="md"
                        >
                            {filteredAuthors.map((author, index) => (
                                <ListItem
                                    key={index}
                                    px={3}
                                    py={2}
                                    fontSize="sm"
                                    cursor="pointer"
                                    _hover={{ bg: suggestionHoverBg }}
                                    onMouseDown={() => handleAuthorSelect(author)}
                                >
                                    {author}
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Apply Button */}
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        size="sm"
                        colorScheme="green"
                        bg="customGreen.500"
                        _hover={{ bg: "customGreen.600" }}
                        onClick={handleApplyFilters}
                        isDisabled={!hasUnappliedChanges}
                    >
                        Terapkan
                    </Button>
                </Box>
            </VStack>
        </Box>
    );
};
